import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import {
  Box,
  CircularProgress,
  CssBaseline,
  Grid2,
  TextField,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import logo from "../../assets/logo-without-bg.png";
import { userStore } from "../../stores/sharedStores";
import AppButton from "../components/common/buttons/AppButton";
import CustomTypography from "../components/common/form/CustomTypography";
import InlineFormError from "../components/common/form/InlineFormError";
import OtpCodeInput from "../components/login/OtpCodeInput";
import {
  getIdentityErrorMessage,
  getRetryAfterSeconds,
  isValidEmail,
  isValidPhone,
  maskEmail,
  maskPhone,
  normalizeCountryCode,
} from "../components/login/loginErrors";

type LoginStep = "identifier" | "otp";
type IdentifierMethod = "email" | "phone";

const OTP_RESEND_SECONDS = 30;

const isMobileOtpEnabled =
  process.env.REACT_APP_ENABLE_MOBILE_OTP?.toLowerCase() === "true";

const COMMANDER_HIGHLIGHTS = [
  {
    title: "Operations",
    body: "Daily tasks, TAT, and work queues in one place.",
    Icon: AssignmentOutlinedIcon,
  },
  {
    title: "Analytics",
    body: "Lead, finance, and ops dashboards for the team.",
    Icon: InsightsOutlinedIcon,
  },
  {
    title: "Fulfillment",
    body: "Orders, shipments, and inventory from request to delivery.",
    Icon: LocalShippingOutlinedIcon,
  },
] as const;

const GoogleMark = () => (
  <Box
    component="span"
    sx={{
      width: 18,
      height: 18,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.26-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  </Box>
);

const Login = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const colors = theme.palette.customColors;

  const [step, setStep] = useState<LoginStep>("identifier");
  const [method, setMethod] = useState<IdentifierMethod>("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const autoSubmitting = useRef(false);

  const pageBg = isDark ? "#0F172A" : colors.pageBackground;
  const cardBg = isDark ? "#1E293B" : colors.white;
  const titleColor = isDark ? "#F1F5F9" : colors.darkBlueText;
  const subtitleColor = isDark ? "#94A3B8" : colors.greyTextColor;
  const fieldBorder = isDark ? "#334155" : colors.lightGray;

  const identifierValid = useMemo(() => {
    if (method === "email") return isValidEmail(email);
    return (
      isValidPhone(phone) &&
      /^\+\d{1,4}$/.test(normalizeCountryCode(countryCode))
    );
  }, [countryCode, email, method, phone]);

  const canSendOtp = identifierValid && !loading;

  const destinationLabel = useMemo(() => {
    if (method === "email" || !isMobileOtpEnabled) {
      return maskEmail(email.trim());
    }
    return maskPhone(phone.trim(), normalizeCountryCode(countryCode));
  }, [countryCode, email, method, phone]);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 44,
      borderRadius: "4px",
      backgroundColor: colors.white,
      "& fieldset": { borderColor: fieldBorder },
      "&:hover fieldset": { borderColor: colors.purpleMain },
      "&.Mui-focused fieldset": { borderColor: colors.darkBlue },
    },
    "& .MuiOutlinedInput-input": {
      py: "10px",
      fontSize: "0.8rem",
    },
  };

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (!isMobileOtpEnabled && method === "phone") {
      setMethod("email");
      setPhone("");
      setInlineError("");
    }
  }, [method]);

  const useEmailOtp = method === "email" || !isMobileOtpEnabled;

  const otpRequest = () =>
    useEmailOtp
      ? { email: email.trim() }
      : {
          phone: phone.trim(),
          countryCode: normalizeCountryCode(countryCode),
        };

  const verifyRequest = (code: string) =>
    useEmailOtp
      ? { email: email.trim(), otp: code }
      : {
          phone: phone.trim(),
          countryCode: normalizeCountryCode(countryCode),
          otp: code,
        };

  const startResendCountdown = (seconds = OTP_RESEND_SECONDS) => {
    setResendSeconds(seconds);
  };

  const handleGoogleLogin = async () => {
    try {
      setInlineError("");
      setLoading(true);
      const authUrl = await userStore.userAPI.getGoogleAuthUrl();
      if (authUrl && typeof authUrl === "string") {
        window.location.href = authUrl;
        return;
      }
      setInlineError("Google auth URL is missing from backend response.");
    } catch (error) {
      setInlineError(getIdentityErrorMessage(error, "google"));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!canSendOtp) return;
    try {
      setInlineError("");
      setLoading(true);
      await userStore.sendLoginOtp(otpRequest());
      setOtp("");
      setStep("otp");
      startResendCountdown();
    } catch (error) {
      if (
        (error as { response?: { status?: number } })?.response?.status === 429
      ) {
        startResendCountdown(getRetryAfterSeconds(error));
      }
      setInlineError(getIdentityErrorMessage(error, "send"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code = otp) => {
    if (code.length !== 6 || loading || autoSubmitting.current) return;
    try {
      autoSubmitting.current = true;
      setInlineError("");
      setLoading(true);
      const user = await userStore.verifyLoginOtp(verifyRequest(code));
      if (user?.token) {
        window.location.href = "/swageazy";
        return;
      }
      setInlineError("Login failed. Please try again later.");
    } catch (error) {
      setInlineError(getIdentityErrorMessage(error, "verify"));
    } finally {
      autoSubmitting.current = false;
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendSeconds > 0 || loading) return;
    try {
      setInlineError("");
      setLoading(true);
      await userStore.sendLoginOtp(otpRequest());
      setOtp("");
      startResendCountdown();
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 429) {
        startResendCountdown(getRetryAfterSeconds(error));
      }
      if (status === 403 || status === 503) {
        setStep("identifier");
      }
      setInlineError(getIdentityErrorMessage(error, "send"));
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentIdentifier = () => {
    setStep("identifier");
    setOtp("");
    setInlineError("");
    autoSubmitting.current = false;
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        overflowX: "hidden",
        backgroundColor: pageBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1.5, sm: 3 },
        pt: {
          xs: "max(12px, env(safe-area-inset-top))",
          sm: "max(24px, env(safe-area-inset-top))",
        },
        pb: {
          xs: "max(12px, env(safe-area-inset-bottom))",
          sm: "max(24px, env(safe-area-inset-bottom))",
        },
      }}
    >
      <CssBaseline />
      <Box
        sx={{
          width: "100%",
          maxWidth: 920,
          borderRadius: { xs: "12px", md: "12px" },
          overflow: "hidden",
          border: `1px solid ${isDark ? "#334155" : colors.lightGray}`,
          boxShadow: `0 8px 24px ${colors.shadowVeryLight}`,
          backgroundColor: cardBg,
        }}
      >
        <Grid2 container>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                minHeight: { xs: "auto", md: 560 },
                height: "100%",
                backgroundColor: "#1C1B3B",
                px: { xs: 2.25, md: 3.5 },
                py: { xs: 2, md: 4.5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: { xs: 1.25, md: 2 },
              }}
            >
              <Box>
                <CustomTypography
                  variant="heading9"
                  color="#A89CF7"
                  style={{
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: "0.4rem",
                  }}
                >
                  Swageazy
                </CustomTypography>
                <CustomTypography
                  variant="heading3"
                  color="#FFFFFF"
                  style={{ letterSpacing: "-0.01em", marginBottom: "0.45rem" }}
                >
                  Commander
                </CustomTypography>
                <CustomTypography variant="body2" color="#BDB9D7">
                  The ops admin for merch programs — from daily tasks to
                  shipments.
                </CustomTypography>
              </Box>

              <Grid2
                container
                spacing={1.25}
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                {COMMANDER_HIGHLIGHTS.map(({ title, body, Icon }) => (
                  <Grid2 key={title} size={{ xs: 12 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                        px: 1.25,
                        py: 1.1,
                        borderRadius: "8px",
                        backgroundColor: "rgba(99,91,237,0.22)",
                        borderLeft: "3px solid #6355E7",
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          minWidth: 28,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(255,255,255,0.08)",
                          color: "#A89CF7",
                          mt: "1px",
                        }}
                      >
                        <Icon sx={{ fontSize: 16 }} />
                      </Box>
                      <Box>
                        <CustomTypography
                          variant="heading8"
                          color="#FFFFFF"
                          style={{ marginBottom: "0.15rem" }}
                        >
                          {title}
                        </CustomTypography>
                        <CustomTypography variant="body2" color="#BDB9D7">
                          {body}
                        </CustomTypography>
                      </Box>
                    </Box>
                  </Grid2>
                ))}
              </Grid2>

              <Grid2
                container
                spacing={1}
                sx={{ display: { xs: "flex", sm: "none" } }}
              >
                {COMMANDER_HIGHLIGHTS.map(({ title }) => (
                  <Grid2 key={title} size={{ xs: 4 }}>
                    <Box
                      sx={{
                        textAlign: "center",
                        px: 0.5,
                        py: 0.75,
                        borderRadius: "999px",
                        backgroundColor: "rgba(99,91,237,0.22)",
                        color: "#FFFFFF",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                      }}
                    >
                      {title}
                    </Box>
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 7 }}>
            <Box
              sx={{
                px: { xs: 2.25, sm: 3.2, md: 3.8 },
                py: { xs: 2.5, sm: 3.2, md: 3.8 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: { md: 560 },
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Swageazy"
                sx={{
                  height: { xs: 36, md: 42 },
                  width: "auto",
                  objectFit: "contain",
                  objectPosition: "left center",
                  mb: 0.75,
                }}
              />
              {step === "identifier" ? (
                <>
                  <CustomTypography
                    variant="heading3"
                    color={titleColor}
                    style={{
                      marginBottom: "0.35rem",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Sign in
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color={subtitleColor}
                    style={{ marginBottom: "1.25rem" }}
                  >
                    {isMobileOtpEnabled
                      ? "Use Google or a one-time code sent to your work email or phone."
                      : "Use Google or a one-time code sent to your work email."}
                  </CustomTypography>

                  <AppButton
                    variant="transparentPrimary"
                    label="Continue with Google"
                    startIcon={<GoogleMark />}
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    customStyles={{ width: "100%", mb: 2 }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{ flex: 1, height: "1px", bgcolor: fieldBorder }}
                    />
                    <CustomTypography variant="heading8" color={subtitleColor}>
                      or continue with OTP
                    </CustomTypography>
                    <Box
                      sx={{ flex: 1, height: "1px", bgcolor: fieldBorder }}
                    />
                  </Box>

                  {isMobileOtpEnabled && (
                    <Box
                      sx={{
                        display: "flex",
                        p: 0.5,
                        mb: 2,
                        borderRadius: "999px",
                        backgroundColor: isDark ? "#334155" : colors.lightBlue,
                        width: "100%",
                      }}
                    >
                      {(["email", "phone"] as IdentifierMethod[]).map(
                        (item) => {
                          const selected = method === item;
                          return (
                            <Box
                              key={item}
                              component="button"
                              type="button"
                              onClick={() => {
                                setMethod(item);
                                setInlineError("");
                              }}
                              sx={{
                                flex: 1,
                                height: 34,
                                border: "none",
                                borderRadius: "999px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                backgroundColor: selected
                                  ? colors.purpleMain
                                  : "transparent",
                                color: selected ? colors.white : titleColor,
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                fontFamily: "inherit",
                              }}
                            >
                              {item === "email" ? "Email" : "Phone"}
                            </Box>
                          );
                        },
                      )}
                    </Box>
                  )}

                  {method === "email" || !isMobileOtpEnabled ? (
                    <Box sx={{ mb: 1.5 }}>
                      <CustomTypography
                        variant="heading8"
                        color={subtitleColor}
                        style={{ marginBottom: "0.4rem" }}
                      >
                        Work email
                      </CustomTypography>
                      <TextField
                        fullWidth
                        placeholder="admin@swageazy.com"
                        value={email}
                        autoComplete="email"
                        onChange={(event) => setEmail(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") void handleSendOtp();
                        }}
                        sx={fieldSx}
                      />
                    </Box>
                  ) : (
                    <Grid2 container spacing={1} sx={{ mb: 1.5 }}>
                      <Grid2 size={{ xs: 2.5, sm: 2 }}>
                        <CustomTypography
                          variant="heading8"
                          color={subtitleColor}
                          style={{ marginBottom: "0.4rem" }}
                        >
                          Code
                        </CustomTypography>
                        <TextField
                          fullWidth
                          value={countryCode}
                          onChange={(event) =>
                            setCountryCode(event.target.value)
                          }
                          sx={fieldSx}
                          disabled
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 9.5, sm: 10 }}>
                        <CustomTypography
                          variant="heading8"
                          color={subtitleColor}
                          style={{ marginBottom: "0.4rem" }}
                        >
                          Mobile number
                        </CustomTypography>
                        <TextField
                          fullWidth
                          placeholder="9876543210"
                          value={phone}
                          inputProps={{ inputMode: "numeric", maxLength: 10 }}
                          onChange={(event) =>
                            setPhone(event.target.value.replace(/\D/g, ""))
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter") void handleSendOtp();
                          }}
                          sx={fieldSx}
                        />
                      </Grid2>
                    </Grid2>
                  )}

                  <InlineFormError message={inlineError} />

                  <AppButton
                    variant="primary"
                    label={
                      loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        "Send code"
                      )
                    }
                    onClick={handleSendOtp}
                    disabled={!canSendOtp}
                    customStyles={{ width: "100%", mt: 0.5 }}
                  />
                </>
              ) : (
                <>
                  <CustomTypography
                    variant="heading3"
                    color={titleColor}
                    style={{
                      marginBottom: "0.35rem",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Enter the 6-digit code
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color={subtitleColor}
                    style={{ marginBottom: "1.25rem" }}
                  >
                    Sent to {destinationLabel}
                  </CustomTypography>

                  <Box sx={{ mb: 2 }}>
                    <OtpCodeInput
                      value={otp}
                      disabled={loading}
                      error={Boolean(inlineError)}
                      onChange={setOtp}
                      onComplete={(code) => {
                        void handleVerifyOtp(code);
                      }}
                    />
                  </Box>

                  <InlineFormError message={inlineError} />

                  <AppButton
                    variant="primary"
                    label={
                      loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        "Verify"
                      )
                    }
                    onClick={() => {
                      void handleVerifyOtp();
                    }}
                    disabled={otp.length !== 6 || loading}
                    customStyles={{ width: "100%", mb: 1.5 }}
                  />

                  <CustomTypography
                    variant="body2"
                    color={subtitleColor}
                    style={{ marginBottom: "0.75rem" }}
                  >
                    {resendSeconds > 0
                      ? `Didn’t get it? Resend in 0:${String(resendSeconds).padStart(2, "0")}`
                      : "Didn’t get it?"}
                    {resendSeconds === 0 && (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          void handleResend();
                        }}
                        sx={{
                          ml: 0.75,
                          border: "none",
                          background: "none",
                          p: 0,
                          color: colors.darkBlue,
                          fontWeight: 700,
                          fontSize: "inherit",
                          fontFamily: "inherit",
                          cursor: loading ? "default" : "pointer",
                        }}
                      >
                        Resend
                      </Box>
                    )}
                  </CustomTypography>

                  <Box
                    component="button"
                    type="button"
                    onClick={handleUseDifferentIdentifier}
                    sx={{
                      border: "none",
                      background: "none",
                      p: 0,
                      cursor: "pointer",
                      width: "fit-content",
                      color: colors.purpleMain,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      fontFamily: "inherit",
                    }}
                  >
                    {isMobileOtpEnabled
                      ? "Use a different email/phone"
                      : "Use a different email"}
                  </Box>
                </>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default observer(Login);
